import { useQuery } from '@tanstack/react-query';
import type { MeResponse } from '@teraseya/api-types';
import { ApiError, apiFetch } from './api-client';

/**
 * Token storage. Key 'token' matches v1 so a v1 user with an existing
 * localStorage token still works after switching to v2 on the same origin.
 */
const TOKEN_KEY = 'token';

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  const t = getToken();
  return typeof t === 'string' && t.length > 0;
}

export const ME_QUERY_KEY = ['me'] as const;

/**
 * Fetch the authenticated user's profile. Cached for 60s so admin nav links
 * and gates don't refetch on every navigation. Disabled when there's no
 * token in storage to avoid a guaranteed 401 round trip on the login page.
 *
 * 401s are surfaced as errors and NOT retried — the caller (e.g. an admin
 * route's `beforeLoad`) is responsible for clearing the token and redirecting.
 */
export function useMe() {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => apiFetch<MeResponse>('/private/auth/me'),
    enabled: isAuthenticated(),
    staleTime: 60_000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 401) return false;
      return failureCount < 2;
    },
  });
}
